import React, { forwardRef } from 'react';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPrint = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const part = new Intl.DateTimeFormat('en-CA', {
      day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Dubai',
    }).format(d);
    const [y, m, dd] = part.split('-');
    const time = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Dubai', hour12: false }).format(d);
    return `${dd}/${m}/${y} ${time}`;
  } catch { return ''; }
};

const formatHijriPrint = (iso) => {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('ar-SA-u-ca-islamic-umalqura', {
      day: '2-digit', month: 'long', year: 'numeric', timeZone: 'Asia/Dubai',
    }).format(new Date(iso));
  } catch { return ''; }
};

/**
 * Salon-wide last 6 months PDF — landscape A4, one section per month with a
 * per-barber breakdown plus monthly totals. Admin-only printable.
 */
const SalonSixMonthSheet = forwardRef(({ data }, ref) => {
  if (!data || !data.report) return null;
  const printDate = data.generated_at;

  return (
    <div
      ref={ref}
      dir="rtl"
      style={{
        width: '297mm',
        padding: '8mm',
        background: '#ffffff',
        color: '#1a1a1a',
        fontFamily: '"Tajawal", "Cairo", "Segoe UI", Tahoma, sans-serif',
        fontSize: '9pt',
      }}
    >
      {/* Header */}
      <div style={{ borderBottom: '2px solid #7c3aed', paddingBottom: '4mm', marginBottom: '5mm', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontSize: '16pt', fontWeight: 800 }}>KE Gents Saloon</div>
          <div style={{ fontSize: '10pt', color: '#666' }}>تقرير 6 أشهر للصالون كاملاً</div>
        </div>
        <div style={{ textAlign: 'left', fontSize: '9pt', color: '#444' }}>
          <div>تاريخ الطباعة: <strong dir="ltr">{fmtPrint(printDate)}</strong></div>
          <div>هجري: <strong>{formatHijriPrint(printDate)}</strong></div>
        </div>
      </div>

      {/* Per-barber trend ribbon (latest vs prev month) */}
      {data.trends && Object.keys(data.trends).length > 0 && (
        <div style={{ background: '#fffbea', border: '1px solid #f0d56b', borderRadius: '3mm', padding: '3mm 4mm', marginBottom: '5mm' }}>
          <div style={{ fontSize: '10pt', fontWeight: 700, marginBottom: '2mm', color: '#7a5a00' }}>
            اتجاه أداء الحلاقين (آخر شهر مقابل السابق)
          </div>
          <div style={{ display: 'flex', gap: '4mm', flexWrap: 'wrap' }}>
            {Object.values(data.trends).map((t) => {
              const arrow = t.direction === 'up' ? '▲' : t.direction === 'down' ? '▼' : '→';
              const color = t.direction === 'up' ? '#0f7a3f' : t.direction === 'down' ? '#b91c1c' : '#666';
              const sign = t.delta_pct > 0 ? '+' : '';
              return (
                <div key={t.barber_id} style={{ display: 'flex', alignItems: 'center', gap: '2mm', background: '#fff', border: '1px solid #eee', borderRadius: '2mm', padding: '1mm 3mm' }}>
                  <span style={{ fontWeight: 600 }}>{t.barber_name}</span>
                  <span style={{ color, fontWeight: 700 }}>{arrow} {sign}{t.delta_pct}%</span>
                  <span style={{ fontSize: '7.5pt', color: '#999', direction: 'ltr' }}>
                    {Number(t.latest_revenue).toLocaleString('en-US')} ← {Number(t.prev_revenue).toLocaleString('en-US')}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.report.map((m, mi) => (
        <div key={m.month} style={{ marginBottom: '6mm', breakInside: 'avoid', pageBreakInside: 'avoid' }}>
          <div style={{ background: '#f3f0ff', padding: '2mm 3mm', borderRight: '4px solid #7c3aed', marginBottom: '2mm', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong style={{ fontSize: '11pt' }}>شهر {m.month}</strong>
            <span style={{ fontSize: '9pt', color: '#444' }}>
              التزام: <strong>{fmt(m.monthly_commitment)} د.إ</strong>
              {' • '}
              حصة/حلاق: <strong>{fmt(m.commitment_share_per_barber)}</strong>
              {' • '}
              عدد الحلاقين النشطين: <strong>{m.eligible_barbers_count}</strong>
            </span>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8.5pt' }}>
            <thead>
              <tr style={{ background: '#fafafa', color: '#333' }}>
                <th style={{ padding: '2mm', textAlign: 'right', borderBottom: '1px solid #ddd' }}>الحلاق</th>
                <th style={{ padding: '2mm', textAlign: 'left', borderBottom: '1px solid #ddd' }}>إيراد</th>
                <th style={{ padding: '2mm', textAlign: 'left', borderBottom: '1px solid #ddd' }}>حصة التزام</th>
                <th style={{ padding: '2mm', textAlign: 'center', borderBottom: '1px solid #ddd' }}>%</th>
                <th style={{ padding: '2mm', textAlign: 'left', borderBottom: '1px solid #ddd' }}>راتب محسوب</th>
                <th style={{ padding: '2mm', textAlign: 'left', borderBottom: '1px solid #ddd' }}>راتب فعلي (بدون دعم)</th>
                <th style={{ padding: '2mm', textAlign: 'left', borderBottom: '1px solid #ddd' }}>راتب نهائي</th>
                <th style={{ padding: '2mm', textAlign: 'left', borderBottom: '1px solid #ddd' }}>عجز</th>
                <th style={{ padding: '2mm', textAlign: 'center', borderBottom: '1px solid #ddd' }}>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {m.barbers.map((b) => (
                <tr key={b.barber_id} style={{ borderBottom: '1px solid #f0f0f0', color: b.is_excluded ? '#999' : '#1a1a1a' }}>
                  <td style={{ padding: '2mm' }}>
                    {b.barber_name}
                    {b.is_excluded && <span style={{ marginRight: '2mm', fontSize: '7pt', color: '#a16207' }}>(مستثنى)</span>}
                  </td>
                  <td style={{ padding: '2mm', textAlign: 'left', direction: 'ltr' }}>{fmt(b.revenue)}</td>
                  <td style={{ padding: '2mm', textAlign: 'left', direction: 'ltr', color: '#0369a1' }}>−{fmt(b.commitment_share)}</td>
                  <td style={{ padding: '2mm', textAlign: 'center' }}>{b.percentage}%</td>
                  <td style={{ padding: '2mm', textAlign: 'left', direction: 'ltr' }}>{fmt(b.calculated_salary)}</td>
                  <td style={{ padding: '2mm', textAlign: 'left', direction: 'ltr', fontWeight: 700 }}>{fmt(b.natural_salary)}</td>
                  <td style={{ padding: '2mm', textAlign: 'left', direction: 'ltr', fontWeight: 700, color: '#0f7a3f' }}>{fmt(b.final_salary)}</td>
                  <td style={{ padding: '2mm', textAlign: 'left', direction: 'ltr', color: b.salary_deficit > 0 ? '#b91c1c' : '#666' }}>
                    {b.salary_deficit > 0 ? `−${fmt(b.salary_deficit)}` : '—'}
                  </td>
                  <td style={{ padding: '2mm', textAlign: 'center', fontSize: '8pt' }}>
                    {b.status === 'paid' ? '✅ مدفوع' : b.status === 'pending' ? '🕒 معلّق' : '— غير محتسب'}
                  </td>
                </tr>
              ))}
              <tr style={{ background: '#fafafa', fontWeight: 700, borderTop: '2px solid #ccc' }}>
                <td style={{ padding: '2mm' }}>الإجمالي</td>
                <td style={{ padding: '2mm', textAlign: 'left', direction: 'ltr' }}>{fmt(m.total_revenue)}</td>
                <td style={{ padding: '2mm', textAlign: 'left', direction: 'ltr' }}>−{fmt(m.monthly_commitment)}</td>
                <td style={{ padding: '2mm', textAlign: 'center' }}>—</td>
                <td style={{ padding: '2mm', textAlign: 'left' }}>—</td>
                <td style={{ padding: '2mm', textAlign: 'left', direction: 'ltr' }}>{fmt(m.total_natural_salaries)}</td>
                <td style={{ padding: '2mm', textAlign: 'left', direction: 'ltr', color: '#0f7a3f' }}>{fmt(m.total_salaries_paid)}</td>
                <td style={{ padding: '2mm', textAlign: 'left', direction: 'ltr', color: '#b91c1c' }}>−{fmt(m.total_deficit)}</td>
                <td style={{ padding: '2mm', textAlign: 'center', color: m.salon_profit >= 0 ? '#0f7a3f' : '#b91c1c' }}>
                  ربح الصالون: {fmt(m.salon_profit)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ))}

      <div style={{ textAlign: 'center', fontSize: '8pt', color: '#999', marginTop: '4mm', borderTop: '1px solid #eee', paddingTop: '2mm' }}>
        KE Gents Saloon — كل المبالغ بالدرهم الإماراتي (د.إ). تقرير داخلي.
      </div>
    </div>
  );
});

SalonSixMonthSheet.displayName = 'SalonSixMonthSheet';
export default SalonSixMonthSheet;
